import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { io } from "socket.io-client";
import { ToastContainer } from "react-toastify";
import { APP_NAME, CHAT_THEME } from "../../util/brand";
import axios from "axios";
import customAxios from "../../util/customAxios";
import { UserContext } from "../../context/user";
import BugReportForm from "../feedback/BugReportForm";

const FALLBACK_API_BASE = `${window.location.origin}/api`;
const API_URL = import.meta.env.VITE_API_URL || FALLBACK_API_BASE;
const SOCKET_URL = API_URL.replace(/\/api\/?$/, "");

// Safari/older WebKit and non-secure contexts don't expose crypto.randomUUID.
// Fall back to a timestamp + random string id so the chat widget never crashes.
function generateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `wv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId() {
  let id = localStorage.getItem("wv_chat_session");
  if (!id) {
    id = generateId();
    localStorage.setItem("wv_chat_session", id);
  }
  return id;
}

function isImageUrl(text) {
  return /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(text) ||
    text.includes("/api/report-screenshots/");
}

const styles = getStyles(CHAT_THEME);

export default function ChatWidget() {
  const { user } = useContext(UserContext);
  const showBugTab = Boolean(user?.token);
  const [open, setOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("chat"); // chat | bug
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [unread, setUnread] = useState(0);
  const [uploading, setUploading] = useState(false);
  const socketRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const sessionId = getSessionId();
    const socket = io(SOCKET_URL, {
      query: { sessionId },
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => {
      setConnected(true);
      if (user?.token) {
        socket.emit("chat:auth", { token: user.token });
      }
    });
    socket.on("disconnect", () => setConnected(false));

    socket.on("chat:message", (msg) => {
      setMessages((prev) => [...prev, msg]);
      setUnread((prev) => prev + 1);
    });

    socketRef.current = socket;
    return () => socket.disconnect();
  }, [user?.token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!open) {
      setPanelMode("chat");
      return;
    }
    setUnread(0);
    if (panelMode === "chat") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, panelMode]);

  useEffect(() => {
    if (!showBugTab && panelMode === "bug") setPanelMode("chat");
  }, [showBugTab, panelMode]);

  const sendText = (text) => {
    if (!text || !socketRef.current) return;
    socketRef.current.emit("chat:send", { text });
    setMessages((prev) => [
      ...prev,
      { text, from: "visitor", timestamp: Date.now() },
    ]);
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    sendText(text);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API_URL}/basic/chat-screenshot`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.url) {
        sendText(res.data.url);
      }
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadImage(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) uploadImage(file);
        break;
      }
    }
  };

  const renderMessageContent = (msg) => {
    const text = msg.text || "";
    if (isImageUrl(text)) {
      return (
        <a href={text} target="_blank" rel="noopener noreferrer">
          <img
            src={text}
            alt="Shared image"
            style={{
              maxWidth: "100%",
              maxHeight: 180,
              borderRadius: 8,
              display: "block",
            }}
          />
        </a>
      );
    }
    return text;
  };

  const headerSubtitle =
    showBugTab && panelMode === "bug"
      ? "Bug reports go to the same channel as Help → Send Feedback."
      : connected
        ? "We typically reply in minutes"
        : "Connecting...";

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={styles.trigger}
          aria-label="Open chat"
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          {unread > 0 && <span style={styles.badge}>{unread}</span>}
        </button>
      )}

      {open && (
        <div style={styles.panel}>
          <div style={styles.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={styles.avatar}>
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </div>
              <div>
                <div style={styles.headerTitle}>{APP_NAME}</div>
                <div style={styles.headerSub}>{headerSubtitle}</div>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} style={styles.closeBtn} aria-label="Close chat">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {showBugTab && (
            <div style={styles.tabBar}>
              <button
                type="button"
                onClick={() => setPanelMode("chat")}
                style={{
                  ...styles.tabBtn,
                  ...(panelMode === "chat" ? styles.tabBtnActive : {}),
                }}
              >
                Messages
              </button>
              <button
                type="button"
                onClick={() => setPanelMode("bug")}
                style={{
                  ...styles.tabBtn,
                  ...(panelMode === "bug" ? styles.tabBtnActive : {}),
                }}
              >
                Report bug
              </button>
            </div>
          )}

          <ToastContainer />

          {panelMode === "chat" ? (
            <>
              <div ref={scrollRef} style={styles.messages}>
                {messages.length === 0 && (
                  <div style={styles.welcome}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                      Hi there!
                    </div>
                    <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.5 }}>
                      Have a question about {APP_NAME}? Send us a message and we&apos;ll
                      get back to you shortly.
                    </div>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: msg.from === "visitor" ? "flex-end" : "flex-start",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        ...styles.bubble,
                        ...(msg.from === "visitor" ? styles.bubbleVisitor : styles.bubbleReply),
                      }}
                    >
                      {msg.from === "human" && (
                        <div style={styles.replyLabel}>Brian</div>
                      )}
                      {msg.from === "ai" && (
                        <div style={styles.replyLabel}>AI Assistant</div>
                      )}
                      {renderMessageContent(msg)}
                    </div>
                  </div>
                ))}
                {uploading && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                    <div style={{ ...styles.bubble, ...styles.bubbleVisitor, opacity: 0.7 }}>
                      Uploading image...
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.inputArea} onPaste={handlePaste}>
                <input
                  type="file"
                  ref={fileRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  style={styles.attachBtn}
                  aria-label="Attach image"
                  disabled={uploading}
                >
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={send}
                  disabled={!input.trim() || uploading}
                  style={{
                    ...styles.sendBtn,
                    opacity: input.trim() && !uploading ? 1 : 0.4,
                  }}
                  aria-label="Send message"
                >
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div ref={scrollRef} style={{ ...styles.messages, padding: "12px 14px" }} className="bg-white dark:bg-gray-900">
              <BugReportForm
                compact
                includeClientIdentityFields={false}
                submitFeedback={(fd) =>
                  customAxios.post(`${API_URL}/basic/user-feedback`, fd, {
                    headers: {
                      "Content-Type": "multipart/form-data",
                      ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
                    },
                  })
                }
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

function getStyles(theme) {
  return {
  trigger: {
    position: "fixed",
    bottom: 24,
    left: 24,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: theme.accent,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 4px 20px ${theme.accentShadow}`,
    zIndex: 9999,
    transition: "transform 0.15s ease",
  },
  badge: {
    position: "absolute",
    top: -4,
    right: -4,
    background: "#ef4444",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    width: 20,
    height: 20,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  panel: {
    position: "fixed",
    bottom: 24,
    left: 24,
    width: 380,
    maxWidth: "calc(100vw - 32px)",
    height: 520,
    maxHeight: "calc(100vh - 48px)",
    borderRadius: 16,
    background: "#fff",
    boxShadow: "0 8px 40px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    zIndex: 9999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  tabBar: {
    display: "flex",
    gap: 8,
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
    flexShrink: 0,
  },
  tabBtn: {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 10,
    border: "none",
    background: "#e5e7eb",
    color: "#374151",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  tabBtnActive: {
    background: theme.tabActiveBg,
    color: theme.tabActiveText,
    boxShadow: `inset 0 0 0 1px ${theme.tabActiveBorder}`,
  },
  header: {
    background: theme.accent,
    color: "#fff",
    padding: "16px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 15,
  },
  headerSub: {
    fontSize: 12,
    opacity: 0.85,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    padding: 4,
    opacity: 0.8,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    minHeight: 0,
  },
  welcome: {
    textAlign: "center",
    padding: "32px 16px",
  },
  bubble: {
    maxWidth: "80%",
    padding: "10px 14px",
    borderRadius: 14,
    fontSize: 14,
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  bubbleVisitor: {
    background: theme.accent,
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  bubbleReply: {
    background: "#f3f4f6",
    color: "#1f2937",
    borderBottomLeftRadius: 4,
  },
  replyLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    marginBottom: 2,
  },
  inputArea: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "12px 12px 12px 8px",
    borderTop: "1px solid #e5e7eb",
    flexShrink: 0,
    background: "#fff",
  },
  attachBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "none",
    color: "#6b7280",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "color 0.15s",
  },
  input: {
    flex: 1,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  },
  sendBtn: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: theme.accent,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  };
}
